import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as DocumentPointIcon } from 'assets/document-point-icon.svg';
import { ReactComponent as ResendIcon } from 'assets/send-email.svg';
import { ReactComponent as CopyLinkIcon } from 'assets/iconButtons/copy-link-icon.svg';
import i18nContext from 'components/i18n-context';
import { formatDateTime } from 'services/utils';
import { IconButton } from 'uikit/IconButton/IconButton';
import { Tooltip } from 'uikit/Tooltip/Tooltip';
import './DocumentRequestItem.scss';

// Mapping of document types to their i18n label keys
const DOCUMENT_TYPE_LABELS = {
  INVOICE: 'transactionDetails.documentRequest.type.invoice',
  AGREEMENT: 'transactionDetails.documentRequest.type.agreement',
  TRANSPORTATION_DOCUMENTS: 'transactionDetails.documentRequest.type.transportationDocuments',
  PROOF_OF_WORK: 'transactionDetails.documentRequest.type.proofOfWork',
  BANK_STATEMENT: 'transactionDetails.documentRequest.type.bankStatement',
  TAX_DECLARATION: 'transactionDetails.documentRequest.type.taxDeclaration',
  IDENTIFICATION_DOCUMENT: 'transactionDetails.documentRequest.type.identificationDocument',
  PROOF_OF_ADDRESS: 'transactionDetails.documentRequest.type.proofOfAddress',
  PROOF_OF_DIVIDENDS: 'transactionDetails.documentRequest.type.proofOfDividends',
  PURPOSE_OF_PAYMENT: 'transactionDetails.documentRequest.type.purposeOfPayment',
  OTHER: 'transactionDetails.documentRequest.type.other'
};

const DocumentRequestItem = ({
  request,
  requestLink,
  onComplete,
  onCancel,
  onResend,
  onDownload,
  isLoading,
  initialExpanded = false
}) => {
  const i18n = useContext(i18nContext);
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'REQUESTED':
        return 'status-requested';
      case 'PROVIDED':
        return 'status-provided';
      case 'COMPLETED':
        return 'status-completed';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'REQUESTED':
        return i18n.getMessage('transactionDetails.documentRequest.status.requested');
      case 'PROVIDED':
        return i18n.getMessage('transactionDetails.documentRequest.status.provided');
      case 'COMPLETED':
        return i18n.getMessage('transactionDetails.documentRequest.status.completed');
      case 'CANCELLED':
        return i18n.getMessage('transactionDetails.documentRequest.status.cancelled');
      default:
        return status;
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const canComplete = request.status === 'PROVIDED';
  const canCancel = request.status === 'REQUESTED' || request.status === 'PROVIDED';
  const canResend = request.status === 'REQUESTED';
  const canCopyLink = request.status === 'REQUESTED' || request.status === 'PROVIDED';
  const isCopyLinkDisabled = isLoading || !requestLink;

  const handleCopyLink = () => {
    if (requestLink) {
      navigator.clipboard.writeText(requestLink).then(
        () => {
          setShowCopyTooltip(true);
          setTimeout(() => {
            setShowCopyTooltip(false);
          }, 2000);
        },
        () => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = requestLink;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            setShowCopyTooltip(true);
            setTimeout(() => {
              setShowCopyTooltip(false);
            }, 2000);
          } catch (error) {
            // Silent fail
          }
          document.body.removeChild(textArea);
        }
      );
    }
  };

  // Calculate upload progress
  const totalItems = request.items ? request.items.length : 0;
  const uploadedItems = request.items ? request.items.filter((item) => item.submittedAt).length : 0;

  return (
    <div className='document-request-item without-border'>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className='request-header'
        onClick={request.items && request.items.length > 0 ? handleToggleExpand : undefined}
        onKeyDown={
          request.items && request.items.length > 0 ? (e) => e.key === 'Enter' && handleToggleExpand() : undefined
        }
        role={request.items && request.items.length > 0 ? 'button' : undefined}
        tabIndex={request.items && request.items.length > 0 ? 0 : undefined}
        style={request.items && request.items.length > 0 ? { cursor: 'pointer' } : { cursor: 'default' }}
      >
        <div className='header-info'>
          <div className='request-title'>
            <span className='title-text'>
              {i18n.getMessage('transactionDetails.documentRequest.request')}
              {totalItems > 0 && (
                <span className='upload-progress'>
                  {' '}
                  ({uploadedItems}/{totalItems} {i18n.getMessage('transactionDetails.documentRequest.itemsUploaded')})
                </span>
              )}
            </span>
            <span className='expiration-date'>
              {i18n.getMessage('transactionDetails.documentRequest.expirationDate')}:{' '}
              {formatDateTime(request.expiresAt)}
            </span>
          </div>
          <div className={`status-badge ${getStatusColor(request.status)}`}>
            <span className='status-dot' />
            <span className='status-label'>{getStatusLabel(request.status)}</span>
          </div>
        </div>

        <div className='header-actions'>
          {canCopyLink && (
            <Tooltip
              text={
                showCopyTooltip
                  ? i18n.getMessage('transactionDetails.documentRequest.linkCopied')
                  : requestLink
                    ? i18n.getMessage('transactionDetails.documentRequest.action.copyLink')
                    : i18n.getMessage('transactionDetails.documentRequest.action.copyLinkUnavailable')
              }
              show={showCopyTooltip ? true : undefined}
              wide={!showCopyTooltip && !requestLink}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role='presentation'
            >
              <IconButton
                className='copy-link-button'
                Icon={CopyLinkIcon}
                onClick={() => {
                  handleCopyLink();
                }}
                isDisabled={isCopyLinkDisabled}
              />
            </Tooltip>
          )}
          {canComplete && (
            <Tooltip text={i18n.getMessage('transactionDetails.documentRequest.action.complete')}>
              <button
                className='icon-button complete-button'
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete(request.id);
                }}
                disabled={isLoading}
              >
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M5.78947 7.88889L7.68421 9.72222L14 3.61111M13.3684 8.5V12.7778C13.3684 13.1019 13.2353 13.4128 12.9985 13.642C12.7616 13.8712 12.4403 14 12.1053 14H3.26316C2.92815 14 2.60686 13.8712 2.36997 13.642C2.13308 13.4128 2 13.1019 2 12.7778V3.22222C2 2.89807 2.13308 2.58719 2.36997 2.35798C2.60686 2.12877 2.92815 2 3.26316 2H10.2105'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </Tooltip>
          )}

          {canCancel && (
            <Tooltip text={i18n.getMessage('transactionDetails.documentRequest.action.cancel')}>
              <button
                className='icon-button cancel-button'
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(request.id);
                }}
                disabled={isLoading}
              >
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M3 4.4H4.11111M4.11111 4.4H13M4.11111 4.4V12.8C4.11111 13.1183 4.22817 13.4235 4.43655 13.6485C4.64492 13.8736 4.92754 14 5.22222 14H10.7778C11.0725 14 11.3551 13.8736 11.5635 13.6485C11.7718 13.4235 11.8889 13.1183 11.8889 12.8V4.4H4.11111ZM5.77778 4.4V3.2C5.77778 2.88174 5.89484 2.57652 6.10322 2.35147C6.31159 2.12643 6.5942 2 6.88889 2H9.11111C9.4058 2 9.68841 2.12643 9.89679 2.35147C10.1052 2.57652 10.2222 2.88174 10.2222 3.2V4.4M6.88889 7.4V11M9.11111 7.4V11'
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </Tooltip>
          )}

          {canResend && (
            <Tooltip text={i18n.getMessage('transactionDetails.documentRequest.action.resend')}>
              <button
                className='icon-button resend-button'
                onClick={(e) => {
                  e.stopPropagation();
                  onResend(request.id);
                }}
                disabled={isLoading}
              >
                <ResendIcon />
              </button>
            </Tooltip>
          )}

          {request.items && request.items.length > 0 && (
            <button
              className={`expand-button ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand();
              }}
            >
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M6 9L12 15L18 9'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {isExpanded && request.items && (
        <div className='request-details'>
          <div className='items-list'>
            {request.items.map((item, index) => (
              <div key={index} className='request-item'>
                <div className='item-icon-wrapper'>
                  <DocumentPointIcon className='item-icon' />
                </div>
                <div className='item-content'>
                  <div className='item-info'>
                    <span className='item-type'>
                      {item.type === 'OTHER' && item.name
                        ? item.name
                        : DOCUMENT_TYPE_LABELS[item.type]
                          ? i18n.getMessage(DOCUMENT_TYPE_LABELS[item.type])
                          : item.type}
                    </span>
                    {/* Show upload status indicator */}
                    {item.submittedAt ? (
                      <span className='status-indicator provided'>
                        {i18n.getMessage('transactionDetails.documentRequest.status.uploaded')}
                      </span>
                    ) : (
                      <span className='status-indicator pending'>
                        {i18n.getMessage('transactionDetails.documentRequest.status.pending')}
                      </span>
                    )}
                  </div>
                  {item.files && item.files.length > 0 ? (
                    <div className='files-list'>
                      {item.files.map((file) => (
                        <div key={file.id} className='file-item'>
                          <span
                            className='file-name clickable'
                            onClick={() => onDownload && onDownload(file.id)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && onDownload && onDownload(file.id)}
                            title={i18n.getMessage('transactionDetails.documentRequest.downloadFile')}
                          >
                            {file.name}
                          </span>
                          <span className='file-size'>({Math.round(file.size / 1024)} KB)</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    item.submittedAt && (
                      <div className='no-files-message'>
                        {i18n.getMessage('transactionDetails.documentRequest.noFilesUploaded')}
                      </div>
                    )
                  )}
                  {item.comment && (
                    <div className='item-comment'>
                      <span className='comment-label'>
                        {i18n.getMessage('transactionDetails.documentRequest.commentLabel')}
                      </span>
                      <span className='comment-value'>{item.comment}</span>
                    </div>
                  )}
                  {item.submittedAt && (
                    <div className='item-comment'>
                      <span className='comment-label'>
                        {i18n.getMessage('transactionDetails.documentRequest.uploadDateLabel')}
                      </span>
                      <span className='comment-value'>{formatDateTime(item.submittedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

DocumentRequestItem.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    expiresAt: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        name: PropTypes.string,
        comment: PropTypes.string,
        submittedAt: PropTypes.string,
        files: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            size: PropTypes.number.isRequired
          })
        )
      })
    )
  }).isRequired,
  requestLink: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  isLoading: PropTypes.bool,
  initialExpanded: PropTypes.bool
};

export default DocumentRequestItem;
